import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import {
  SUPPORTED_LANGUAGES,
  PRIVACY_POLICY_VERSION,
  getBundle,
  getCountriesForLocale,
  SUPPORTED_COUNTRY_CODES,
} from "@/lib/signupI18n";
import { partnerCodeStrings, normalizePartnerCode } from "@/lib/partnerCodeI18n";
import { getAuthMessages } from "@/lib/authMessages";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal";
import { ContactAccountFields, getContactCopy } from "@/components/ContactAccountFields";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Field({ label, type = "text", value, onChange, placeholder, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
        data-testid={testId}
      />
    </label>
  );
}

function PasswordField({ label, value, onChange, placeholder, testId, showLabel, hideLabel }) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{label}</span>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="new-password"
          className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-2 text-[11px] font-medium text-[color:var(--brand)]"
        >
          {visible ? hideLabel : showLabel}
        </button>
      </div>
    </label>
  );
}

function InlineError({ text }) {
  return (
    <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]">
      {text}
    </div>
  );
}

export default function SignUpPanel({ locale, setLocale }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const b = getBundle(locale);
  const pc = partnerCodeStrings(locale);
  const authT = getAuthMessages(locale);
  const contactT = getContactCopy(locale);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  // Optional Partner Code (beta access today; ACCOUNT_TIER_RULES.md, 2026-10-30).
  const [partnerCode, setPartnerCode] = useState("");

  const [policyOpened, setPolicyOpened] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [consentChoice, setConsentChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const accepted = consentChoice === "accept";
  const declined = consentChoice === "decline";
  const fieldsValid = [firstName, lastName, streetAddress, city, region, postalCode, telephone].every((v) => v.trim().length > 0)
    && SUPPORTED_COUNTRY_CODES.includes(country)
    && EMAIL_RE.test(email.trim())
    && password.length >= 8
    && password === confirm
    && confirm.length > 0;
  const canSubmit = fieldsValid && ageConfirmed && accepted && !busy;

  const chooseAccept = () => {
    setErr("");
    if (!policyOpened) {
      setErr(b.privacyMustReadFirst);
      return;
    }
    setConsentChoice("accept");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!firstName.trim()) return setErr(b.firstNameRequired);
    if (!lastName.trim() || !streetAddress.trim() || !city.trim() || !region.trim() || !postalCode.trim() || !telephone.trim()) {
      return setErr(contactT.contactPurpose);
    }
    if (!SUPPORTED_COUNTRY_CODES.includes(country)) return setErr(b.countryRequired);
    if (!EMAIL_RE.test(email.trim())) return setErr(b.invalidEmail);
    if (password.length < 8) return setErr(b.passwordTooShort);
    if (password !== confirm) return setErr(b.passwordMismatch);
    if (!ageConfirmed) return setErr(contactT.ageConfirm);
    if (!accepted) return setErr(b.declineMessage);

    setBusy(true);
    try {
      await api.post("/auth/register-contact", {
        email: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        region: region.trim(),
        postal_code: postalCode.trim(),
        country,
        telephone: telephone.trim(),
        preferred_language: locale,
        age_18_or_older: true,
        ...(partnerCode ? { partner_code: partnerCode } : {}),
        privacy_consent: {
          accepted: true,
          policy_version: PRIVACY_POLICY_VERSION,
          policy_language: locale,
          accepted_at: new Date().toISOString(),
          age_of_majority_confirmed: true,
        },
      });
      toast.success(authT.accountCreated);
      await refresh();
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-5 space-y-4" data-testid="signup-panel" data-locale={locale}>
        <div className="rounded-2xl border border-[color:var(--border-stone)] p-3 bg-[color:var(--bg-surface-hover)]">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[color:var(--text-primary)] mb-1">{b.langSelectorLabel}</span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm"
              data-testid="signup-language"
            >
              {SUPPORTED_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
            </select>
          </label>
        </div>

        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">{b.signUpTitle}</h1>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">{contactT.contactPurpose}</p>
        </div>

        <form className="space-y-3" onSubmit={submit}>
          <Field label={b.firstNameLabel} value={firstName} onChange={setFirstName} placeholder={b.firstNamePlaceholder} testId="signup-first-name" autoComplete="given-name" />
          <ContactAccountFields
            locale={locale}
            lastName={lastName} setLastName={setLastName}
            streetAddress={streetAddress} setStreetAddress={setStreetAddress}
            city={city} setCity={setCity}
            region={region} setRegion={setRegion}
            postalCode={postalCode} setPostalCode={setPostalCode}
            telephone={telephone} setTelephone={setTelephone}
          />

          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{b.countryLabel}</span>
            <select required value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm bg-white" data-testid="signup-country" autoComplete="country">
              <option value="" disabled>{b.countryPlaceholder}</option>
              {getCountriesForLocale(locale).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>

          <Field label={b.emailLabel} type="email" value={email} onChange={setEmail} placeholder={b.emailPlaceholder} testId="signup-email" autoComplete="email" />
          <PasswordField label={b.passwordLabel} value={password} onChange={setPassword} placeholder={b.passwordPlaceholder} testId="signup-password" showLabel={authT.showPassword} hideLabel={authT.hidePassword} />
          <PasswordField label={b.confirmPasswordLabel} value={confirm} onChange={setConfirm} placeholder={b.confirmPasswordLabel} testId="signup-password-confirm" showLabel={authT.showPassword} hideLabel={authT.hidePassword} />
          {confirm.length > 0 && password !== confirm && <InlineError text={b.passwordMismatch} />}

          <label className="block">
            <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{pc.label}</span>
            <input type="text" value={partnerCode} onChange={(e) => setPartnerCode(normalizePartnerCode(e.target.value))} placeholder={pc.placeholder} maxLength={32} autoComplete="off" spellCheck={false} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm font-mono tracking-wider" data-testid="signup-partner-code" />
            <span className="block text-[11px] text-[color:var(--text-secondary)] mt-1">{pc.hint}</span>
          </label>

          <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${ageConfirmed ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--border-stone)]"}`}>
            <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]" data-testid="signup-age-confirm" />
            <span className="text-xs text-[color:var(--text-primary)] leading-snug">{contactT.ageConfirm}</span>
          </label>

          <div className="rounded-2xl border border-[color:var(--border-stone)] p-4 space-y-3 bg-white">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold">{b.privacySectionTitle}</div>
            <div className="text-[11px] text-[color:var(--text-secondary)]">{b.policyVersionLabel}: <span className="font-mono">{PRIVACY_POLICY_VERSION}</span> · {locale}</div>
            <button type="button" onClick={() => { setPolicyModalOpen(true); setPolicyOpened(true); }} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-xs font-medium">{b.privacyReviewCta}</button>
            <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${accepted ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--border-stone)]"}`}>
              <input type="checkbox" checked={accepted} onChange={chooseAccept} className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]" data-testid="consent-optin" />
              <span className="text-xs leading-snug">{b.optInLabel}</span>
            </label>
            <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${declined ? "border-[color:var(--score-bad)] bg-[color:var(--score-bad)]/5" : "border-[color:var(--border-stone)]"}`}>
              <input type="checkbox" checked={declined} onChange={() => { setErr(""); setConsentChoice("decline"); }} className="mt-0.5 h-4 w-4 accent-[color:var(--score-bad)]" />
              <span className="text-xs leading-snug">{b.optOutLabel}</span>
            </label>
            {declined && <InlineError text={b.declineMessage} />}
          </div>

          {err && <InlineError text={err} />}
          <button type="submit" disabled={!canSubmit} className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40" data-testid="signup-submit">
            {busy ? b.submitBusy : b.submitCta}
          </button>
        </form>
      </div>

      {policyModalOpen && <PrivacyPolicyModal locale={locale} onClose={() => setPolicyModalOpen(false)} />}
    </>
  );
}
